<template>
    <div class="container mx-auto">
        <steps-viewer
            :steps="$data.$steps"
            :step="currentStep"
            @click="clickStep"
        />

        <div
            v-if="currentStep === 0"
            class="w-full md:w-10/12 lg:w-8/12 mx-auto"
        >
            <div class="flex flex-col justify-center">
                <div class="flex items-center justify-center mb-4">
                    <span class="display-4 mr-2">💰</span>
                    <p
                        class="text-theme-primary text-xl font-semibold"
                        data-testid="default-typography"
                    >
                        Sube el archivo con los precios
                    </p>
                </div>

                <div
                    class="flex items-center justify-between p-4 border rounded-xl mb-4"
                >
                    <div class="flex-1 text-sm text-theme-base pr-4">
                        <p data-testid="default-typography">
                            Completa el archivo con los nuevos precios. Solo se
                            actualizan referencias ya existentes, identificadas
                            por código de barras. Máximo
                            <strong>500 filas</strong>.
                        </p>
                    </div>
                    <a
                        href="/template_massive_update_products_references_prices.xlsx"
                        target="_blank"
                        rel="noopener"
                        class="flex-shrink-0 flex items-center gap-2 px-4 py-2 border-2 border-theme-success text-theme-success font-semibold rounded-lg hover:bg-theme-hover transition-colors text-sm"
                    >
                        ⬇ Descargar plantilla
                    </a>
                </div>

                <massive-file-loader
                    :parser="parser"
                    @loaded="loadPrices"
                    @loading="isLoading = true"
                    @error="isLoading = false"
                />
            </div>
        </div>

        <div v-else-if="currentStep === 1" class="flex flex-col">
            <div class="flex flex-col items-center">
                <p data-testid="default-typography" class="my-4 font-bold">
                    Tienes errores en el archivo. Puedes corregir el archivo y
                    volver al paso anterior, o descartar las filas con error y
                    continuar con las filas válidas.
                </p>
                <div class="flex gap-3">
                    <app-button relief @click="resetItem">
                        Volver al paso anterior
                    </app-button>
                    <app-button warning @click="discardErrorRowsAndContinue">
                        Descartar filas con error y continuar
                    </app-button>
                </div>
            </div>
            <error-viewer
                v-for="(errorItem, index) in errors"
                :key="index"
                :error="errorItem"
                :active="activeError"
                :base="$data.$errorsBase"
                @changeActive="changeActiveError($event)"
            />
        </div>

        <div v-else-if="currentStep === 2">
            <div class="my-3 flex items-center gap-2">
                <span class="text-lg font-semibold"
                    >Variantes a actualizar:</span
                >
                <span class="text-theme-primary font-bold text-lg">
                    {{ referencesFound.length }}
                </span>
            </div>

            <reference-price-manage
                v-for="(item, index) in referencesFound"
                :key="index"
                :reference="item"
                :update-enabled="true"
                :delete-enabled="true"
                @update="updateReference"
                @remove="removeReference"
            />

            <div class="my-4 flex justify-center">
                <app-button
                    class="mt-3 mb-5 w-3/4"
                    border
                    gradient
                    @click="saveMassiveReferencesPricesComponent"
                >
                    <span class="text-lg"> Guardar cambios ✔ </span>
                </app-button>
            </div>
        </div>

        <div v-else-if="[3, 4].includes(currentStep)">
            <div v-if="isLoading">Procesando ...</div>
            <div v-else class="flex flex-wrap">
                <div class="w-full mb-4">
                    <span class="text-theme-primary text-xl">Resultados</span>
                </div>
                <div class="w-1/2 mt-2">
                    <strong class="h5">
                        Variantes actualizadas:
                        <span class="text-theme-primary">
                            {{ results.referencesUpdated.length }}
                        </span>
                    </strong>
                </div>
                <div class="w-1/2 mt-2">
                    <strong class="h5">
                        Productos padre actualizados:
                        <span class="text-theme-primary">
                            {{ productsUpdatedCount }}
                        </span>
                    </strong>
                </div>
                <div class="w-full flex justify-center mt-4">
                    <app-button @click="resetItem">
                        Iniciar nueva carga
                    </app-button>
                    <app-button success @click="downloadJsonComponent">
                        Descargar Resultados JSON ⬇
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { parseFileMassivePrices } from "@/common/lib/excel";
import { downloadJson } from "@/common/lib/json";

import ReferencePriceManage from "@/components/references/ReferencePriceManage.vue";
import ErrorViewer from "@/components/utils/ErrorViewer.vue";
import MassiveFileLoader from "@/components/utils/MassiveFileLoader.vue";
import StepsViewer from "@/components/utils/StepsViewer.vue";

export default {
    name: "MassiveUpdatePrices",
    components: {
        MassiveFileLoader,
        StepsViewer,
        ErrorViewer,
        ReferencePriceManage
    },
    data: () => ({
        parser: parseFileMassivePrices,
        isLoading: false,
        lastRows: [],
        $steps: [
            { name: "Sube archivo", enabledClicked: true },
            { name: "Valida errores en archivo" },
            { name: "Verifica los precios" },
            { name: "Guarda los cambios" }
        ],
        $errorsBase: [
            { name: 'Código de barras "barcode"', key: "barcode" },
            { name: 'Precio de venta "priceOffer"', key: "priceOffer" },
            { name: 'Precio anterior "price"', key: "price" },
            { name: 'Costo "cost"', key: "cost" }
        ],
        currentStep: 0,
        activeError: 0,
        referencesFound: [],
        errors: [],
        results: {
            referencesUpdated: []
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        productsUpdatedCount() {
            return this.results.referencesUpdated.filter(
                (r) => r.productUpdated
            ).length;
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        await verifyPagePermission("product:LoadMassive", "/app/products");
    },
    methods: {
        ...mapActions("products", [
            "verifyMassiveReferencesPrices",
            "saveMassiveReferencesPrices"
        ]),
        async loadPrices(rows) {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: `Vas a cargar ${rows.length} ${
                    rows.length < 2 ? "fila" : "filas"
                } ?`,
                icon: "info",
                showCancelButton: true,
                focusConfirm: false,
                confirmButtonText: "Si",
                cancelButtonText: "No",
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#d33ddd",
                allowOutsideClick: false
            });
            if (!dialogResult.isConfirmed) {
                window.location.href = this.$route.path;
                return;
            }
            this.lastRows = rows;
            this.verifyMassiveReferencesPricesComponent(rows);
        },
        async verifyMassiveReferencesPricesComponent(rows) {
            this.errors = [];
            this.referencesFound = [];
            this.isLoading = true;
            this.$loading.show();
            try {
                const result = await this.verifyMassiveReferencesPrices({
                    rows
                });
                if (!result.pass) {
                    this.errors = result.errors;
                    this.activeError = result.errors[0].index;
                    this.currentStep = 1;
                    return;
                }
                this.referencesFound = result.referencesFound;
                this.currentStep = 2;
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async discardErrorRowsAndContinue() {
            const errorIndexes = new Set(this.errors.map((e) => e.index));
            const filteredRows = this.lastRows.filter(
                (r) => !errorIndexes.has(r.index)
            );
            if (!filteredRows.length) {
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "No quedan filas válidas para procesar",
                    icon: "info"
                });
                this.resetItem();
                return;
            }
            this.verifyMassiveReferencesPricesComponent(filteredRows);
        },
        async saveMassiveReferencesPricesComponent() {
            if (!this.referencesFound.length) {
                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "No hay referencias que guardar. ¿Deseas volver al paso inicial?",
                    icon: "info",
                    showCancelButton: true,
                    focusConfirm: false,
                    confirmButtonText: "No hacer nada",
                    cancelButtonText: "Volver al paso inicial",
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#113ea2",
                    allowOutsideClick: false,
                    showCloseButton: true
                });
                if (!dialogResult.isConfirmed) {
                    this.resetItem();
                }
                return;
            }
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: `Vas a actualizar los precios de ${
                    this.referencesFound.length
                } ${
                    this.referencesFound.length < 2
                        ? "referencia"
                        : "referencias"
                } ?`,
                icon: "info",
                showCancelButton: true,
                focusConfirm: false,
                confirmButtonText: "Si",
                cancelButtonText: "No",
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#d33ddd",
                allowOutsideClick: false
            });
            if (!dialogResult.isConfirmed) return;

            this.isLoading = true;
            this.$loading.show();
            this.currentStep = 3;
            const references = this.referencesFound.map((ref, index) => ({
                index: index + 1,
                item: {
                    barcode: ref.barcode,
                    price: ref.newPrice,
                    priceOffer: ref.newPriceOffer,
                    cost: ref.newCost
                }
            }));
            try {
                this.results = await this.saveMassiveReferencesPrices({
                    references
                });
                this.isLoading = false;
                this.$loading.hide();
                this.currentStep = 4;
                this.$sound.play();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                this.currentStep = 2;
            }
        },
        removeReference({ barcode }) {
            const index = this.referencesFound.findIndex(
                (r) => r.barcode === barcode
            );
            if (index === -1) return;
            const updated = [...this.referencesFound];
            updated.splice(index, 1);
            this.referencesFound = updated;
        },
        updateReference(payload) {
            const index = this.referencesFound.findIndex(
                (r) => r.barcode === payload.barcode
            );
            if (index === -1) return;
            const updated = [...this.referencesFound];
            updated[index] = { ...payload };
            this.referencesFound = updated;
        },
        clickStep(step) {
            this.currentStep = step;
        },
        changeActiveError(ref) {
            this.activeError = ref;
        },
        resetItem() {
            this.errors = [];
            this.referencesFound = [];
            this.lastRows = [];
            this.currentStep = 0;
            this.results = { referencesUpdated: [] };
        },
        downloadJsonComponent() {
            downloadJson({
                payload: this.results,
                filename: "massiveUpdatePricesResult"
            });
        }
    }
};
</script>
